import { useState } from "react";
import {
    useTriggerOsmImport,
    useTriggerLantmaterietImport,
    useTriggerElectoralDistrictImport,
    useTriggerMunicipalityImport,
    useTriggerMunicipalityFromElectoralDistricts,
    useTriggerPostalCodeImport,
    useTriggerSpatialAssignment,
    useTriggerAddressGridAggregations,
    useTriggerElectoralMetadataFill,
    useTriggerElectoralMetadataFill2018,
    type OsmImportRequest,
    type LantmaterietImportRequest,
    type ElectoralDistrictImportRequest,
    type MunicipalityImportRequest,
    type MunicipalityFromElectoralDistrictsRequest,
    type PostalCodeImportRequest,
    type SpatialAssignmentRequest,
    type AddressGridAggregationsRequest,
} from "../api/imports";
import { fetchCampaigns, type Campaign } from "../api/campaigns";
import { getHangfireUrl } from "../api/hangfire";
import { useQuery } from "@tanstack/react-query";
import { showSuccess, showError } from "../shared/toast";
import "./hangfire-jobs-panel.css";

interface JobCardProps {
    title: string;
    description: string;
    badge: string;
    notes: string;
    onNotesChange: (notes: string) => void;
    onTrigger: () => Promise<void>;
    isPending: boolean;
    buttonText: string;
}

const JobCard = ({
    title,
    description,
    badge,
    notes,
    onNotesChange,
    onTrigger,
    isPending,
    buttonText,
}: JobCardProps) => {
    return (
        <div className="hangfire-job-card">
            <div className="hangfire-job-card__header">
                <h3>{title}</h3>
                <span className="hangfire-job-card__badge">{badge}</span>
            </div>
            <p className="hangfire-job-card__description">{description}</p>
            <div className="hangfire-job-card__form">
                <label htmlFor={`${title.toLowerCase().replace(/\s+/g, "-")}-notes`}>
                    Anteckningar (valfritt):
                </label>
                <textarea
                    id={`${title.toLowerCase().replace(/\s+/g, "-")}-notes`}
                    value={notes}
                    onChange={(e) => onNotesChange(e.target.value)}
                    rows={2}
                    placeholder="Lägg till anteckningar om jobbet..."
                    className="hangfire-job-card__notes"
                />
                <button
                    className="hangfire-job-card__trigger-button"
                    onClick={onTrigger}
                    disabled={isPending}
                >
                    {isPending ? "Köar..." : buttonText}
                </button>
            </div>
        </div>
    );
};

export const HangfireJobsPanel = (): JSX.Element => {
    const [osmNotes, setOsmNotes] = useState("");
    const [lmNotes, setLmNotes] = useState("");
    const [edNotes, setEdNotes] = useState("");
    const [muniNotes, setMuniNotes] = useState("");
    const [muniFromEdNotes, setMuniFromEdNotes] = useState("");
    const [pcNotes, setPcNotes] = useState("");
    const [spatialNotes, setSpatialNotes] = useState("");
    const [aggregationsNotes, setAggregationsNotes] = useState("");
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
    const [fillMunicipalityId, setFillMunicipalityId] = useState("");
    const [fillLanId, setFillLanId] = useState("");
    const [fillMode, setFillMode] = useState<"municipality" | "lan">("municipality");
    const [fill2018MunicipalityId, setFill2018MunicipalityId] = useState("");
    const [fill2018LanId, setFill2018LanId] = useState("");
    const [fill2018Mode, setFill2018Mode] = useState<"municipality" | "lan">("municipality");

    const osmMutation = useTriggerOsmImport();
    const lmMutation = useTriggerLantmaterietImport();
    const edMutation = useTriggerElectoralDistrictImport();
    const muniMutation = useTriggerMunicipalityImport();
    const muniFromEdMutation = useTriggerMunicipalityFromElectoralDistricts();
    const pcMutation = useTriggerPostalCodeImport();
    const spatialMutation = useTriggerSpatialAssignment();
    const aggregationsMutation = useTriggerAddressGridAggregations();
    const electoralMetadataFillMutation = useTriggerElectoralMetadataFill();
    const electoralMetadataFill2018Mutation = useTriggerElectoralMetadataFill2018();

    // Fetch campaigns for dropdown
    const { data: campaigns = [] } = useQuery<Campaign[]>({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch Hangfire URL for opening dashboard
    const { data: hangfireUrl } = useQuery({
        queryKey: ["hangfire", "url"],
        queryFn: getHangfireUrl,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    const handleOsmImport = async () => {
        try {
            const request: OsmImportRequest = {
                notes: osmNotes || undefined,
            };
            const result = await osmMutation.mutateAsync(request);
            showSuccess(`OSM import köad. Job ID: ${result.jobId}`);
            setOsmNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte köa OSM import";
            showError("Fel", message);
        }
    };

    const handleLmImport = async () => {
        try {
            const request: LantmaterietImportRequest = {
                notes: lmNotes || undefined,
            };
            const result = await lmMutation.mutateAsync(request);
            showSuccess(`Lantmäteriet import köad. Job ID: ${result.jobId}`);
            setLmNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte köa Lantmäteriet import";
            showError("Fel", message);
        }
    };

    const handleEdImport = async () => {
        try {
            const request: ElectoralDistrictImportRequest = {
                notes: edNotes || undefined,
            };
            const result = await edMutation.mutateAsync(request);
            showSuccess(`Valdistrikt import köad. Job ID: ${result.jobId}`);
            setEdNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte köa valdistrikt import";
            showError("Fel", message);
        }
    };

    const handleMunicipalityImport = async () => {
        try {
            const request: MunicipalityImportRequest = {
                source: "kommuner",
                notes: muniNotes || undefined,
            };
            const result = await muniMutation.mutateAsync(request);
            showSuccess(`Kommunimport köad. Job ID: ${result.jobId}`);
            setMuniNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte köa kommunimport";
            showError("Fel", message);
        }
    };

    const handleMunicipalityFromElectoralDistricts = async () => {
        try {
            const request: MunicipalityFromElectoralDistrictsRequest = {
                notes: muniFromEdNotes || undefined,
            };
            const result = await muniFromEdMutation.mutateAsync(request);
            showSuccess(`Kommunpolygoner från valdistrikt köad. Job ID: ${result.jobId}`);
            setMuniFromEdNotes("");
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                "Kunde inte köa kommunpolygoner från valdistrikt";
            showError("Fel", message);
        }
    };

    const handlePcImport = async () => {
        try {
            const request: PostalCodeImportRequest = {
                notes: pcNotes || undefined,
            };
            const result = await pcMutation.mutateAsync(request);
            showSuccess(`Postnummer import köad. Job ID: ${result.jobId}`);
            setPcNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte köa postnummer import";
            showError("Fel", message);
        }
    };

    const handleSpatialAssignment = async () => {
        try {
            const request: SpatialAssignmentRequest = {
                notes: spatialNotes || undefined,
            };
            const result = await spatialMutation.mutateAsync(request);
            showSuccess(`Spatial assignments köad. Job ID: ${result.jobId}`);
            setSpatialNotes("");
        } catch (error: any) {
            const message =
                error?.response?.data?.message || "Kunde inte köa spatial assignments";
            showError("Fel", message);
        }
    };

    const handleAddressGridAggregations = async () => {
        if (!selectedCampaignId) {
            showError("Fel", "Välj en campaign");
            return;
        }
        try {
            const request: AddressGridAggregationsRequest = {
                campaignId: selectedCampaignId,
                notes: aggregationsNotes || undefined,
            };
            const result = await aggregationsMutation.mutateAsync(request);
            showSuccess(`Address grid aggregations köad. Job ID: ${result.jobId}`);
            setAggregationsNotes("");
        } catch (error: any) {
            const message =
                error?.response?.data?.message || "Kunde inte köa address grid aggregations";
            showError("Fel", message);
        }
    };

    const handleElectoralMetadataFill = async () => {
        if (fillMode === "municipality") {
            const municipalityId = fillMunicipalityId.trim();
            if (!municipalityId) {
                showError("Fel", "Ange kommunens Id (Guid)");
                return;
            }
            try {
                const result = await electoralMetadataFillMutation.mutateAsync({ municipalityId });
                if ("jobId" in result) {
                    showSuccess(`Påfyllning valmetadata köad. Job ID: ${result.jobId}`);
                    setFillMunicipalityId("");
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.message || "Kunde inte köa påfyllning valmetadata";
                showError("Fel", message);
            }
        } else {
            const lanId = fillLanId.trim();
            if (!lanId || lanId.length !== 2) {
                showError("Fel", "Ange län-id (2 siffror, t.ex. 01 för Stockholm)");
                return;
            }
            try {
                const result = await electoralMetadataFillMutation.mutateAsync({ lanId });
                if ("count" in result && "jobIds" in result) {
                    showSuccess(
                        `Påfyllning valmetadata köad för län ${result.lanId}: ${result.count} jobb.`
                    );
                    setFillLanId("");
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.message || "Kunde inte köa påfyllning valmetadata";
                showError("Fel", message);
            }
        }
    };

    const handleElectoralMetadataFill2018 = async () => {
        if (fill2018Mode === "municipality") {
            const municipalityId = fill2018MunicipalityId.trim();
            if (!municipalityId) {
                showError("Fel", "Ange kommunens Id (Guid)");
                return;
            }
            try {
                const result = await electoralMetadataFill2018Mutation.mutateAsync({ municipalityId });
                if ("jobId" in result) {
                    showSuccess(`Påfyllning KF18 (val 2018) köad. Job ID: ${result.jobId}`);
                    setFill2018MunicipalityId("");
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.message || "Kunde inte köa påfyllning KF18";
                showError("Fel", message);
            }
        } else {
            const lanId = fill2018LanId.trim();
            if (!lanId || lanId.length !== 2) {
                showError("Fel", "Ange län-id (2 siffror, t.ex. 01 för Stockholm)");
                return;
            }
            try {
                const result = await electoralMetadataFill2018Mutation.mutateAsync({ lanId });
                if ("count" in result && "jobIds" in result) {
                    showSuccess(
                        `Påfyllning KF18 köad för län ${result.lanId}: ${result.count} jobb.`
                    );
                    setFill2018LanId("");
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.message || "Kunde inte köa påfyllning KF18";
                showError("Fel", message);
            }
        }
    };

    const openHangfireDashboard = () => {
        if (hangfireUrl) {
            window.open(hangfireUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <section className="hangfire-jobs-panel" aria-label="Hangfire-jobb">
            <div className="hangfire-jobs-panel__header">
                <h2>Hangfire-jobb</h2>
                <div className="hangfire-jobs-panel__actions">
                    {hangfireUrl && (
                        <button
                            className="hangfire-jobs-panel__dashboard-button"
                            onClick={openHangfireDashboard}
                            title="Öppna Hangfire Dashboard för att se jobb-status"
                        >
                            Öppna Hangfire Dashboard
                        </button>
                    )}
                </div>
            </div>

            <div className="hangfire-jobs-panel__info">
                <p>
                    Använd dessa knappar för att köa Hangfire-jobb. Jobben körs i bakgrunden via Hangfire.
                    Du kan följa status i Hangfire Dashboard.
                </p>
            </div>

            <div className="hangfire-jobs-panel__jobs">
                {/* Electoral Metadata Fill (EU24, KF22, RD22, RF22, Kryss22 from val.se) */}
                <div className="hangfire-job-card">
                    <div className="hangfire-job-card__header">
                        <h3>Påfyllning valmetadata</h3>
                        <span className="hangfire-job-card__badge">Valmetadata</span>
                    </div>
                    <p className="hangfire-job-card__description">
                        Fyller i metadatavärden (EU24, KF22, RD22, RF22, Kryss22) från val.se – antingen för en
                        kommun (ange Guid) eller för hela ett län (ange län-id, 2 siffror). Data sparas under
                        ValResultatCache (t.ex. D:\Sites\CenterReach\data\valresultat).
                    </p>
                    <div className="hangfire-job-card__form">
                        <div style={{ marginBottom: "0.75rem" }}>
                            <label>
                                <input
                                    type="radio"
                                    name="fill-mode"
                                    checked={fillMode === "municipality"}
                                    onChange={() => setFillMode("municipality")}
                                />{" "}
                                En kommun
                            </label>
                            {" · "}
                            <label>
                                <input
                                    type="radio"
                                    name="fill-mode"
                                    checked={fillMode === "lan"}
                                    onChange={() => setFillMode("lan")}
                                />{" "}
                                Hela länet
                            </label>
                        </div>
                        {fillMode === "municipality" ? (
                            <>
                                <label htmlFor="electoral-metadata-fill-municipality-id">
                                    Kommun Id (Guid): <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="electoral-metadata-fill-municipality-id"
                                    type="text"
                                    value={fillMunicipalityId}
                                    onChange={(e) => setFillMunicipalityId(e.target.value)}
                                    placeholder="t.ex. Botkyrka kommun-Id"
                                    style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
                                />
                            </>
                        ) : (
                            <>
                                <label htmlFor="electoral-metadata-fill-lan-id">
                                    Län-id (2 siffror): <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="electoral-metadata-fill-lan-id"
                                    type="text"
                                    value={fillLanId}
                                    onChange={(e) => setFillLanId(e.target.value.replace(/\D/g, "").slice(0, 2))}
                                    placeholder="t.ex. 01 = Stockholm"
                                    style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
                                />
                            </>
                        )}
                        <button
                            className="hangfire-job-card__trigger-button"
                            onClick={handleElectoralMetadataFill}
                            disabled={
                                electoralMetadataFillMutation.isPending ||
                                (fillMode === "municipality" ? !fillMunicipalityId.trim() : fillLanId.trim().length !== 2)
                            }
                        >
                            {electoralMetadataFillMutation.isPending
                                ? "Köar..."
                                : fillMode === "lan"
                                  ? "Köa påfyllning för hela länet"
                                  : "Köa påfyllning valmetadata"}
                        </button>
                    </div>
                </div>

                {/* Electoral Metadata Fill 2018 (KF18 from historik.val.se) */}
                <div className="hangfire-job-card">
                    <div className="hangfire-job-card__header">
                        <h3>Påfyllning valmetadata 2018 (KF18)</h3>
                        <span className="hangfire-job-card__badge">Valmetadata</span>
                    </div>
                    <p className="hangfire-job-card__description">
                        Fyller i endast KF18 (kommunval 2018) från historik.val.se (HTML). Antingen för en
                        kommun (ange Guid) eller för hela ett län (ange län-id, 2 siffror). Kräver att
                        fältdefinition KF18 finns i databasen.
                    </p>
                    <div className="hangfire-job-card__form">
                        <div style={{ marginBottom: "0.75rem" }}>
                            <label>
                                <input
                                    type="radio"
                                    name="fill-2018-mode"
                                    checked={fill2018Mode === "municipality"}
                                    onChange={() => setFill2018Mode("municipality")}
                                />{" "}
                                En kommun
                            </label>
                            {" · "}
                            <label>
                                <input
                                    type="radio"
                                    name="fill-2018-mode"
                                    checked={fill2018Mode === "lan"}
                                    onChange={() => setFill2018Mode("lan")}
                                />{" "}
                                Hela länet
                            </label>
                        </div>
                        {fill2018Mode === "municipality" ? (
                            <>
                                <label htmlFor="electoral-metadata-fill-2018-municipality-id">
                                    Kommun Id (Guid): <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="electoral-metadata-fill-2018-municipality-id"
                                    type="text"
                                    value={fill2018MunicipalityId}
                                    onChange={(e) => setFill2018MunicipalityId(e.target.value)}
                                    placeholder="t.ex. Botkyrka kommun-Id"
                                    style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
                                />
                            </>
                        ) : (
                            <>
                                <label htmlFor="electoral-metadata-fill-2018-lan-id">
                                    Län-id (2 siffror): <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="electoral-metadata-fill-2018-lan-id"
                                    type="text"
                                    value={fill2018LanId}
                                    onChange={(e) =>
                                        setFill2018LanId(e.target.value.replace(/\D/g, "").slice(0, 2))
                                    }
                                    placeholder="t.ex. 01 = Stockholm"
                                    style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
                                />
                            </>
                        )}
                        <button
                            className="hangfire-job-card__trigger-button"
                            onClick={handleElectoralMetadataFill2018}
                            disabled={
                                electoralMetadataFill2018Mutation.isPending ||
                                (fill2018Mode === "municipality"
                                    ? !fill2018MunicipalityId.trim()
                                    : fill2018LanId.trim().length !== 2)
                            }
                        >
                            {electoralMetadataFill2018Mutation.isPending
                                ? "Köar..."
                                : fill2018Mode === "lan"
                                  ? "Köa KF18 för hela länet"
                                  : "Köa påfyllning KF18"}
                        </button>
                    </div>
                </div>

                {/* Address Grid Aggregations */}
                <div className="hangfire-job-card">
                    <div className="hangfire-job-card__header">
                        <h3>Address Grid Aggregations</h3>
                        <span className="hangfire-job-card__badge">Karta</span>
                    </div>
                    <p className="hangfire-job-card__description">
                        Beräknar geohash-baserade aggregations för snabbare kartprestanda vid låga zoom-nivåer (zoom &lt; 12).
                        Pre-computes grid cells med delivery statistics för alla precisioner (3, 4, 5).
                    </p>
                    <div className="hangfire-job-card__form">
                        <label htmlFor="aggregations-campaign">
                            Campaign: <span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                            id="aggregations-campaign"
                            value={selectedCampaignId}
                            onChange={(e) => setSelectedCampaignId(e.target.value)}
                            style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
                        >
                            <option value="">-- Välj campaign --</option>
                            {campaigns.map((campaign) => (
                                <option key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </option>
                            ))}
                        </select>
                        <label htmlFor="aggregations-notes">
                            Anteckningar (valfritt):
                        </label>
                        <textarea
                            id="aggregations-notes"
                            value={aggregationsNotes}
                            onChange={(e) => setAggregationsNotes(e.target.value)}
                            rows={2}
                            placeholder="Lägg till anteckningar om jobbet..."
                            className="hangfire-job-card__notes"
                        />
                        <button
                            className="hangfire-job-card__trigger-button"
                            onClick={handleAddressGridAggregations}
                            disabled={aggregationsMutation.isPending || !selectedCampaignId}
                        >
                            {aggregationsMutation.isPending ? "Köar..." : "Köa Address Grid Aggregations"}
                        </button>
                    </div>
                </div>

                {/* Spatial Assignments */}
                <JobCard
                    title="Spatial Assignments"
                    description="Bygger om kopplingar mellan adresser, vägsegment, valdistrikt och postnummer. Använd detta för att fixa adresser med NULL RoadSegmentId."
                    badge="Spatial"
                    notes={spatialNotes}
                    onNotesChange={setSpatialNotes}
                    onTrigger={handleSpatialAssignment}
                    isPending={spatialMutation.isPending}
                    buttonText="Köa Spatial Assignments"
                />

                {/* OSM Import */}
                <JobCard
                    title="OSM Import"
                    description="Importerar vägar och adresser från OpenStreetMap för valt område."
                    badge="Vägar & Adresser"
                    notes={osmNotes}
                    onNotesChange={setOsmNotes}
                    onTrigger={handleOsmImport}
                    isPending={osmMutation.isPending}
                    buttonText="Köa OSM Import"
                />

                {/* Lantmäteriet Import */}
                <JobCard
                    title="Lantmäteriet Import"
                    description="Importerar adresser från Lantmäteriet baserat på fil-sökväg."
                    badge="Adresser"
                    notes={lmNotes}
                    onNotesChange={setLmNotes}
                    onTrigger={handleLmImport}
                    isPending={lmMutation.isPending}
                    buttonText="Köa Lantmäteriet Import"
                />

                {/* Electoral District Import */}
                <JobCard
                    title="Valdistrikt Import"
                    description="Importerar valdistrikt från fil."
                    badge="Valdistrikt"
                    notes={edNotes}
                    onNotesChange={setEdNotes}
                    onTrigger={handleEdImport}
                    isPending={edMutation.isPending}
                    buttonText="Köa Valdistrikt Import"
                />

                {/* Municipality (Kommun) Import */}
                <JobCard
                    title="Kommunimport"
                    description="Importerar kommungränser (SCB) från GeoJSON. Konfigurera källa under MunicipalityImport:Sources (t.ex. kommuner) i appsettings. GeoJSON kan skapas från TAB med t.ex. ogr2ogr -f GeoJSON kommuner.json Kommun_Sweref99TM.TAB -t_srs EPSG:4326."
                    badge="Karta"
                    notes={muniNotes}
                    onNotesChange={setMuniNotes}
                    onTrigger={handleMunicipalityImport}
                    isPending={muniMutation.isPending}
                    buttonText="Köa Kommunimport"
                />

                {/* Municipality polygons from electoral districts */}
                <JobCard
                    title="Kommunpolygoner från valdistrikt"
                    description="Bygger eller uppdaterar kommunpolygoner genom att unionera valdistriktsgeometrier per kommunkod (fyra första siffror i valdistriktskoden). Kör efter valdistriktsimport för exakta kommungränser."
                    badge="Karta"
                    notes={muniFromEdNotes}
                    onNotesChange={setMuniFromEdNotes}
                    onTrigger={handleMunicipalityFromElectoralDistricts}
                    isPending={muniFromEdMutation.isPending}
                    buttonText="Köa kommunpolygoner från valdistrikt"
                />

                {/* Postal Code Import */}
                <JobCard
                    title="Postnummer Import"
                    description="Importerar postnummer från fil."
                    badge="Postnummer"
                    notes={pcNotes}
                    onNotesChange={setPcNotes}
                    onTrigger={handlePcImport}
                    isPending={pcMutation.isPending}
                    buttonText="Köa Postnummer Import"
                />
            </div>
        </section>
    );
};